// Los tiempos, las fechas y los nombres de los contratos con Santi, en las palabras de la propuesta
// (docs/pelotas/ux-2026-11-03/propuesta/propuesta.md §5.1): el «cuándo» de una fila («10:12», «ayer», «lun», «22 oct»),
// la fecha límite que vence («Vence el jueves», «Venció el lunes»; en el iPad, «El jueves», «Venció») y la línea 2 de la
// cabecera («Te toca: … · Te lo pidió Santi el 31 de octubre · Para el jueves 5»). Puro y probado.
import { t } from '../../../i18n';
import { formatClockTime, formatDiaMes } from '../../../lib/time';
import { diaLocal } from '../chat/agrupar';
import type { ContratoDTO } from './tipos';

export const YO = 'pau';
export const OTRO = 'santi';

const DIA_MS = 86_400_000;
const DIA_SEMANA_CORTO = new Intl.DateTimeFormat('es-ES', { weekday: 'short' });
const DIA_SEMANA = new Intl.DateTimeFormat('es-ES', { weekday: 'long' });
const DIA_MES_CORTO = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short' });

/** Cómo se llama a cada persona del centro delante de Pau. */
export function nombreDe(persona: string): string {
  if (persona === 'pau') return t('oficinaV2.pelotas.pau');
  if (persona === 'santi') return t('oficinaV2.pelotas.santi');
  return persona ? persona.charAt(0).toUpperCase() + persona.slice(1) : '';
}

/** La inicial del avatar: P, S… */
export function inicialDe(persona: string): string {
  return (persona.charAt(0) || '?').toUpperCase();
}

/** Cuánto hace: «30 min», «2 h», «1 día», «25 días» (la tarjeta «Para Santi», hasta la entrega 2). */
export function lleva(desde: string, ahora = Date.now()): string {
  const min = Math.max(0, Math.round((ahora - Date.parse(desde)) / 60_000));
  if (!Number.isFinite(min)) return '';
  if (min < 60) return t('oficinaV2.pelotas.lleva.min', { n: Math.max(1, min) });
  const h = Math.round(min / 60);
  if (h < 24) return t('oficinaV2.pelotas.lleva.h', { n: h });
  const d = Math.round(h / 24);
  return d === 1 ? t('oficinaV2.pelotas.lleva.dia') : t('oficinaV2.pelotas.lleva.dias', { n: d });
}

/** La hora de un mensaje, como en el chat (24 h). */
export const hora = (iso: string): string => formatClockTime(iso);

/** Días de calendario (locales) entre hoy y ese día: 0 hoy, 1 mañana, -1 ayer. Acepta `AAAA-MM-DD` o ISO. */
export function diasHasta(fecha: string, ahora = Date.now()): number {
  const d = /^\d{4}-\d{2}-\d{2}$/.test(fecha) ? new Date(`${fecha}T12:00:00`) : new Date(fecha);
  const dia = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12).getTime();
  const h = new Date(ahora);
  const hoy = new Date(h.getFullYear(), h.getMonth(), h.getDate(), 12).getTime();
  return Math.round((dia - hoy) / DIA_MS);
}

const fechaDe = (f: string): Date => (/^\d{4}-\d{2}-\d{2}$/.test(f) ? new Date(`${f}T12:00:00`) : new Date(f));

/** El «cuándo» de una fila (el último movimiento): «10:12» hoy, «ayer», «lun» esta semana, «22 oct» antes. */
export function cuandoFila(iso: string, ahora = Date.now()): string {
  const d = diasHasta(iso, ahora);
  if (!Number.isFinite(d)) return '';
  if (d >= 0) return hora(iso);
  if (d === -1) return t('oficinaV2.pelotas.fecha.ayer');
  if (d >= -6) return DIA_SEMANA_CORTO.format(new Date(iso));
  return DIA_MES_CORTO.format(new Date(iso));
}

export interface Vence {
  texto: string;
  tono: 'bloqueado' | 'error';
}

/**
 * La fecha límite en una fila, solo si vence en 2 días o menos (o ya venció): «Vence hoy», «Vence mañana», «Vence el
 * jueves» (naranja), «Venció el lunes» (rojo). `corto` (iPad): «Hoy», «Mañana», «El jueves», «Venció». Más lejos, nada:
 * va en la cabecera.
 */
export function venceFila(vence: string | null, ahora = Date.now(), corto = false): Vence | null {
  if (!vence) return null;
  const d = diasHasta(vence, ahora);
  if (!Number.isFinite(d) || d > 2) return null;
  const f = fechaDe(vence);
  if (d < 0) {
    if (corto) return { texto: t('oficinaV2.pelotas.fecha.vencioCorto'), tono: 'error' };
    return { texto: t('oficinaV2.pelotas.fecha.vencioEl', { dia: d >= -6 ? DIA_SEMANA.format(f) : DIA_MES_CORTO.format(f) }), tono: 'error' };
  }
  if (d === 0) return { texto: t(corto ? 'oficinaV2.pelotas.fecha.hoyCorto' : 'oficinaV2.pelotas.fecha.venceHoy'), tono: 'bloqueado' };
  if (d === 1) return { texto: t(corto ? 'oficinaV2.pelotas.fecha.mananaCorto' : 'oficinaV2.pelotas.fecha.venceManana'), tono: 'bloqueado' };
  return { texto: t(corto ? 'oficinaV2.pelotas.fecha.elCorto' : 'oficinaV2.pelotas.fecha.venceEl', { dia: DIA_SEMANA.format(f) }), tono: 'bloqueado' };
}

/** «jueves 5» (esta semana) o «20 de noviembre» (más lejos o más atrás). */
function diaConNumero(f: string, d: number): string {
  const x = fechaDe(f);
  return Math.abs(d) <= 6 ? `${DIA_SEMANA.format(x)} ${x.getDate()}` : formatDiaMes(x.toISOString());
}

/** «hoy», «ayer» o «el 31 de octubre» (cuándo se pidió). */
export function cuandoPedido(iso: string, ahora = Date.now()): string {
  const d = diaLocal(iso);
  if (d === diaLocal(new Date(ahora).toISOString())) return t('oficinaV2.pelotas.fecha.hoy');
  if (d === diaLocal(new Date(ahora - DIA_MS).toISOString())) return t('oficinaV2.pelotas.fecha.ayer');
  return t('oficinaV2.pelotas.fecha.el', { fecha: formatDiaMes(iso) });
}

/** El separador de día de la conversación: «Hoy», «Ayer», «7 de octubre». */
export function etiquetaDia(iso: string, ahora = Date.now()): string {
  const d = diaLocal(iso);
  if (d === diaLocal(new Date(ahora).toISOString())) return t('oficinaV2.chat.hoy');
  if (d === diaLocal(new Date(ahora - DIA_MS).toISOString())) return t('oficinaV2.chat.ayer');
  return formatDiaMes(iso);
}

// Nombres propios que no se ponen en minúscula tras «Te toca:» (las personas y los productos de la oficina).
const NOMBRES_PROPIOS = new Set(['Santi', 'Pau', 'Carlos', 'Calypso', 'Venuz']);

/** «Subir la ruta…» → «subir la ruta…» tras «Te toca:». Respeta siglas («CRM…», «DeepSeek…») y nombres propios. */
export function minusculaInicial(s: string): string {
  const palabra = s.split(/\s/)[0] ?? '';
  if (!palabra || NOMBRES_PROPIOS.has(palabra.replace(/[^\p{L}]/gu, ''))) return s;
  if (palabra.slice(1) !== palabra.slice(1).toLowerCase()) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}

export interface ParteCabecera {
  texto: string;
  /** La etiqueta en blanco 600 delante («Te toca:», «Esperando a Santi:»). */
  etiqueta?: string;
  tono?: 'bloqueado' | 'error';
}

/**
 * La línea 2 de la cabecera de un contrato abierto: lo que falta y de quién (siempre), de dónde viene y la fecha límite.
 * «Te toca: subir la ruta y avisar a Santi · Te lo pidió Santi el 31 de octubre · Para el jueves 5».
 */
export function lineaCabecera(c: Pick<ContratoDTO, 'lado' | 'queFalta' | 'pedidoPor' | 'pedidoEn' | 'vence'>, ahora = Date.now()): ParteCabecera[] {
  const partes: ParteCabecera[] = [
    {
      etiqueta: c.lado === YO ? t('oficinaV2.pelotas.ventana.teToca') : t('oficinaV2.pelotas.ventana.esperandoA', { quien: nombreDe(c.lado) }),
      texto: minusculaInicial(c.queFalta),
    },
  ];
  if (c.pedidoEn) {
    const cuando = cuandoPedido(c.pedidoEn, ahora);
    partes.push({ texto: c.pedidoPor === YO ? t('oficinaV2.pelotas.fecha.seLoPediste', { cuando }) : t('oficinaV2.pelotas.fecha.teLoPidio', { quien: nombreDe(c.pedidoPor), cuando }) });
  }
  if (c.vence) {
    const d = diasHasta(c.vence, ahora);
    if (Number.isFinite(d)) {
      if (d < 0) partes.push({ texto: t('oficinaV2.pelotas.fecha.vencioEl', { dia: diaConNumero(c.vence, d) }), tono: 'error' });
      else if (d === 0) partes.push({ texto: t('oficinaV2.pelotas.fecha.paraHoy'), tono: 'bloqueado' });
      else if (d === 1) partes.push({ texto: t('oficinaV2.pelotas.fecha.paraManana'), tono: 'bloqueado' });
      else partes.push({ texto: t('oficinaV2.pelotas.fecha.paraEl', { dia: diaConNumero(c.vence, d) }), ...(d <= 2 ? { tono: 'bloqueado' as const } : {}) });
    }
  }
  return partes;
}

/** La línea entera, en texto (el `title` de la cabecera cuando solo cabe la primera parte). */
export const lineaCabeceraTexto = (partes: ParteCabecera[]): string => partes.map((p) => (p.etiqueta ? `${p.etiqueta} ${p.texto}` : p.texto)).join(' · ');

